import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { logRender, logEffect } from "../../diag/renderDiag";
import { Stage, Layer, Rect, Transformer, Line, Group, Circle } from "react-konva";
import type Konva from "konva";
import { getVbInsertDetailFromDataTransfer, VB_INSERT_DND_MIME } from "../utils/vbDnd";
import { useTheme } from "../../contexts/ThemeContext";
import type {
  ArrowElement as ArrowEl,
  CameraTrack,
  CameraTransform,
  CircleElement as CircleEl,
  Element,
  FontPair,
  ImageElement as ImageEl,
  LineElement as LineEl,
  MotionPath,
  PolygonElement as PolygonEl,
  RectElement as RectEl,
  RuntimeMedia,
  StarElement as StarEl,
  TextElement as TextEl,
  VideoElement as VideoEl,
} from "../types";
import { applyKeyframeAnimations } from "./utils/keyframeRenderer";
import { isElementEffectivelyVisible } from "../utils/elementVisibility";
import { cameraToStageTransform, DEFAULT_CAMERA_TRANSFORM } from "../engine/cameraEngine";
import { precomputePath, samplePathForRendering } from "../engine/motionPathEngine";
import { isCameraMotionV2Enabled } from "../utils/studioFeatureFlags";
import PathEditorOverlay from "./PathEditorOverlay";
import { BoostplateOverlay } from "./BoostplateOverlay";
import { MaskEditorOverlay } from "../masks/MaskEditorOverlay";
import type { MaskTool, MaskOperation } from "../types/gradientFade";
import { DEFAULT_VECTOR_MASK } from "../masks";
import { computeSafeZoneRect } from "../utils/smartPlaceholders";
import { get3DNodeAttrs, has3DTransformValues } from "./utils/apply3DToNode";
import type { KonvaNodeCommonProps } from "./CanvasStageTypes";
import { isPreviewTraceEnabled } from "./CanvasStageFilters";
import { getStoredDraftPreviewMode } from "../timeline/draftPreviewMode";
import { renderTextElement } from "./CanvasStageElementText";
import { renderImageElement, renderVideoElement } from "./CanvasStageElementMedia";
import {
  hasActiveCanvasFrameDependency,
  resolveCanvasPlaybackPixelRatio,
  resolveVideoFilterPreviewQuality,
} from "./runtimePlaybackBudget";
import { shouldProtectLongVideoPlayback } from "../utils/longVideoRuntime";
import {
  renderArrowElement,
  renderCircleElement,
  renderLineElement,
  renderPolygonElement,
  renderRectElement,
  renderStarElement,
} from "./CanvasStageElementShapes";
import { WebGLFxLayer } from "./WebGLFxLayer";
import type { FxUniforms } from "./webglFx";
import { PatternAnimLayer } from "../patterns/PatternAnimLayer";
import { useMaskDrawing } from "./hooks/useMaskDrawing";
import { useCanvasFrameLoops } from "./hooks/useCanvasFrameLoops";
import { markDirty } from "../engine/RenderCoordinator";

// Blend mode mapping (extracted outside render loop for performance)
const BLEND_MODE_MAP: Record<string, GlobalCompositeOperation> = {
  'normal': 'source-over',
  'multiply': 'multiply',
  'screen': 'screen',
  'overlay': 'overlay',
  'darken': 'darken',
  'lighten': 'lighten',
  'color-dodge': 'color-dodge',
  'color-burn': 'color-burn',
  'hard-light': 'hard-light',
  'soft-light': 'soft-light',
  'difference': 'difference',
  'exclusion': 'exclusion',
};

// Hover box colors by element category
const HOVER_COLOR_MAP: Record<string, string> = {
  text: '#4F8CFF',      // Figma blue for text
  image: '#4F8CFF',     // Figma blue for images
  video: '#4F8CFF',     // Figma blue for videos
  rect: '#4F8CFF',      // Figma blue for shapes
  circle: '#4F8CFF',
  star: '#4F8CFF',
  polygon: '#4F8CFF',
  arrow: '#4F8CFF',
  line: '#4F8CFF',
};

const getHoverColor = (kind: string): string => {
  return HOVER_COLOR_MAP[kind] || '#4F8CFF'; // Fallback to Figma blue
};

/** Fast AABB check: is element potentially visible in the viewport? */
function isInViewport(
  el: { x: number; y: number; width?: number; height?: number; scaleX?: number; scaleY?: number },
  vp: { left: number; top: number; right: number; bottom: number },
): boolean {
  const w = (el.width ?? 200) * Math.abs(el.scaleX ?? 1);
  const h = (el.height ?? 200) * Math.abs(el.scaleY ?? 1);
  // AABB overlap: element rect vs viewport rect
  return el.x + w > vp.left && el.x < vp.right && el.y + h > vp.top && el.y < vp.bottom;
}

interface GuideLine {
  id: string;
  type: 'vertical' | 'horizontal';
  position: number;
  color: string;
  label?: string;
}

interface GroupInfo {
  groupId: string;
  elementIds: string[];
}

export interface CanvasStageProps {
  stageRef: React.RefObject<Konva.Stage>;
  layerRef: React.RefObject<Konva.Layer>;
  canvasW: number;
  canvasH: number;
  scale: number;
  stagePos: { x: number; y: number };
  setStagePos?: (pos: { x: number; y: number }) => void;
  isSelecting: boolean;
  elements: Element[];
  selectedIds: string[];
  editingTextId?: string | null;
  runtime: React.MutableRefObject<RuntimeMedia>;
  onZoom: (delta: number) => void;
  onStageMouseDown: (e: any) => void;
  onStageMouseMove: () => void;
  onStageMouseUp: () => void;
  onNodeDragStart?: (id: string, evt: any) => void;
  onNodeDragMove?: (id: string, evt: any) => void;
  onNodeDragEnd: (id: string, evt: any) => void;
  onNodeTransformStart?: (id: string) => void;
  onNodeTransform?: (ids: string[], anchor: string | null) => void;
  onNodeTransformEnd: (id: string, node: Konva.Node) => void;
  onSelectElement: (id: string, isMulti: boolean) => void;
  updateEl: (id: string, patch: Partial<Element>) => void;
  selectionRect: {x:number;y:number;width:number;height:number;visible:boolean};
  selRectRef: React.RefObject<Konva.Rect>;
  trRef: React.RefObject<Konva.Transformer>;
  smartGuides?: GuideLine[];
  groups?: Map<string, string[]>; // groupId -> elementIds[]
  onContextMenu?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  /** Show visual center guides (debug); default false keeps canvas clean */
  showGuides?: boolean;
  /** Current playhead time for keyframe animations (Phase 1.5) */
  playhead?: number;
  /** Camera viewport transform (Phase 5) */
  cameraTransform?: CameraTransform;
  /** Camera track settings for advanced overlays (Phase 3) */
  cameraTrack?: CameraTrack;
  /** Feature flag for advanced camera UI/overlays */
  cameraMotionV2Enabled?: boolean;
  /** Motion path edit mode (Phase 5) */
  motionPathEdit?: { elementId: string; path: MotionPath } | null;
  onMotionPathChange?: (elementId: string, path: MotionPath) => void;
  onExitMotionPathEdit?: () => void;
  /** Full-screen preview mode: disables editing UI/handlers */
  previewMode?: boolean;
  /** Rendering layout; `plain` is used for the dedicated Preview page (no editor chrome). */
  layout?: 'editor' | 'plain';
  /** Optional project font pairing for template text. */
  fontPair?: FontPair;
  /** Active crop mode (image/video). */
  cropMode?: { elementId: string; showGuides?: boolean; guideMode?: string } | null;
  /** Timeline clips for motion timing - used to determine element's timeline position */
  clips?: Array<{ elementId: string; start: number; end: number; [key: string]: any }>;
  /** Mask edit mode for gradient fade feature */
  maskEditMode?: {
    elementId: string;
    tool: MaskTool;
    operation: MaskOperation;
  } | null;
  onMaskEditChange?: (elementId: string, settings: any) => void;
  onExitMaskEdit?: () => void;
  /** Whether timeline is actively playing (forces per-frame re-render) */
  isPlaying?: boolean;
  /** Transition effects computed by TimelineShell (ref for zero-copy reads) */
  transitionEffectsRef?: React.MutableRefObject<Map<string, { opacity: number; offsetX: number; offsetY: number; scale: number; rotation: number; clipPath: ((ctx: CanvasRenderingContext2D) => void) | null }>>;
  /** Set of element IDs currently being dragged. When an element is in this set,
   *  React does NOT set x/y/rotation/scale props — Konva's imperative drag position is authoritative.
   *  Without this, React reconciliation overwrites the drag position on every re-render. */
  activeDragRef?: React.RefObject<Set<string>>;
}

function CanvasStage({
  stageRef,
  layerRef,
  canvasW,
  canvasH,
  scale,
  stagePos,
  setStagePos,
  isSelecting,
  elements,
  selectedIds,
  editingTextId,
  runtime,
  onZoom,
  onStageMouseDown,
  onStageMouseMove,
  onStageMouseUp,
  onNodeDragStart,
  onNodeDragMove,
  onNodeDragEnd,
  onNodeTransformStart,
  onNodeTransform,
  onNodeTransformEnd,
  onSelectElement,
  updateEl,
  selectionRect,
  selRectRef,
  trRef,
  smartGuides = [],
  groups,
  showGuides = false,
  onContextMenu,
  playhead = 0,
  cameraTransform,
  cameraTrack,
  cameraMotionV2Enabled,
  motionPathEdit = null,
  onMotionPathChange,
  onExitMotionPathEdit,
  previewMode = false,
  layout = 'editor',
  fontPair,
  cropMode = null,
  clips,
  maskEditMode = null,
  onMaskEditChange,
  onExitMaskEdit,
  isPlaying = false,
  transitionEffectsRef,
  activeDragRef,
}: CanvasStageProps) {
  // Theme system
  const { darkMode } = useTheme();
  const useEditorPalette = true;
  const isLightWorkspace = !darkMode;

  // WebGL FX compositing layer (behind feature flag, off by default)
  const webGLFxEnabled = useMemo(() => localStorage.getItem('vb:studio:webglFx') === '1', []);
  const [globalFxUniforms] = useState<FxUniforms>({ brightness: 1, contrast: 1, saturation: 1 });

  // DEV-only render diagnostics (toggle via localStorage.setItem('vb_debug_render', '1'))
  logRender('CanvasStage', {
    selectedIds,
    scale,
    zoom: scale,
    elements,
    stagePos,
    playhead,
    onStageMouseDown,
    onStageMouseMove,
    onStageMouseUp,
    onNodeDragStart,
    onNodeDragMove,
    onNodeDragEnd,
    onNodeTransformEnd,
    onSelectElement,
    onZoom,
    updateEl,
    onContextMenu,
    onMotionPathChange,
    onExitMotionPathEdit,
  });

  const elementsById = useMemo(() => new Map(elements.map((e) => [e.id, e] as const)), [elements]);
  const previewTraceEnabled = isPreviewTraceEnabled();

  // Mask editing state
  const isMaskEditing = !!maskEditMode && !!maskEditMode.elementId;
  const maskEditElement = isMaskEditing ? elementsById.get(maskEditMode.elementId) : null;

  const {
    isDrawingMask,
    activeMaskPath,
    handleMaskMouseDown,
    handleMaskMouseMove,
    handleMaskMouseUp,
    handleMaskDblClick,
    handleMaskPointDrag,
    handleMaskHandleDrag,
    handleGradientHandleDrag,
  } = useMaskDrawing({
    maskEditMode,
    isMaskEditing,
    maskEditElement: maskEditElement ?? null,
    scale,
    onMaskEditChange,
    onExitMaskEdit,
  });

  const protectedLongVideoElementIds = useMemo(() => {
    const next = new Set<string>();
    for (const element of elements) {
      if (
        element.kind === 'video' &&
        shouldProtectLongVideoPlayback({
          durationSec: element.preparedMedia?.duration,
          previewUrl: element.previewUrl,
          srcUrl: element.src,
          deferVideoHydration: element.deferVideoHydration,
          isPlaying,
        })
      ) {
        next.add(element.id);
      }
    }
    return next;
  }, [elements, isPlaying]);

  const { faceBlurCacheRef, gradientFadeCacheRef } = useCanvasFrameLoops(
    elements,
    runtime,
    {
      isPlaying,
      protectedElementIds: protectedLongVideoElementIds,
    }
  );

  // Background palette cache for media "Contain + Background Extension" mode
  const bgPaletteRef = React.useRef(new Map<string, { avg: string; tl: string; br: string }>());

  const getBgPalette = React.useCallback(
    (
      key: string,
      source: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement | undefined | null
    ): { avg: string; tl: string; br: string } => {
      if (!key) key = "unknown";
      const cached = bgPaletteRef.current.get(key);
      if (cached) return cached;

      if (!source) return { avg: "#111111", tl: "#111111", br: "#000000" };

      try {
        const canvas = document.createElement("canvas");
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D | null;
        if (!ctx) return { avg: "#111111", tl: "#111111", br: "#000000" };

        ctx.drawImage(source as CanvasImageSource, 0, 0, 16, 16);
        const data = ctx.getImageData(0, 0, 16, 16).data;

        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 16) continue;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
        if (count < 1) count = 1;
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);

        const px = (x: number, y: number) => {
          const ix = Math.max(0, Math.min(15, x));
          const iy = Math.max(0, Math.min(15, y));
          const off = (iy * 16 + ix) * 4;
          return { r: data[off], g: data[off + 1], b: data[off + 2], a: data[off + 3] };
        };

        const tl = px(2, 2);
        const br = px(13, 13);

        const toHex = (c: number) => c.toString(16).padStart(2, "0");
        const rgbHex = (rr: number, gg: number, bb: number) => `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;

        const result = {
          avg: rgbHex(r, g, b),
          tl: rgbHex(tl.r, tl.g, tl.b),
          br: rgbHex(br.r, br.g, br.b),
        };

        bgPaletteRef.current.set(key, result);
        return result;
      } catch {
        return { avg: "#111111", tl: "#111111", br: "#000000" };
      }
    },
    []
  );

  const cameraLayerTransform = useMemo(() => {
    return cameraToStageTransform(cameraTransform ?? DEFAULT_CAMERA_TRANSFORM, canvasW, canvasH);
  }, [cameraTransform, canvasH, canvasW]);

  // Set high quality image smoothing on the layer canvas for better video/image quality
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    const canvas = layer.getCanvas();
    if (!canvas) return;

    // Get the native 2D context for quality settings via public API
    const ctx = canvas.getContext();
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    }
  }, [layerRef]);

  const totalViewportScale = useMemo(() => {
    const camScale = cameraTransform?.scale ?? 1;
    return (scale || 1) * (Number.isFinite(camScale) ? camScale : 1);
  }, [cameraTransform?.scale, scale]);
  const allowCameraMotionV2 = cameraMotionV2Enabled ?? isCameraMotionV2Enabled();
  const activeCropId = cropMode?.elementId ?? null;
  const cropGuidesEnabled = !!cropMode?.showGuides;
  const cropGuideMode = cropMode?.guideMode ?? "thirds";
  const isCropActive = !!activeCropId;

  // Memoized grid lines (avoids recreation on every render)
  const gridLines = useMemo(() => {
    const lines: React.ReactNode[] = [];
    const gridSize = 50;
    const lineColor = "rgba(100, 150, 255, 0.3)";
    const strokeWidth = 1 / scale;
    for (let x = gridSize; x < canvasW; x += gridSize) {
      lines.push(
        <Line key={`v-${x}`} points={[x, 0, x, canvasH]} stroke={lineColor} strokeWidth={strokeWidth} listening={false} perfectDrawEnabled={false} />
      );
    }
    for (let y = gridSize; y < canvasH; y += gridSize) {
      lines.push(
        <Line key={`h-${y}`} points={[0, y, canvasW, y]} stroke={lineColor} strokeWidth={strokeWidth} listening={false} perfectDrawEnabled={false} />
      );
    }
    return lines;
  }, [canvasW, canvasH, scale]);

  const isMotionPathEditing = !!(!previewMode && motionPathEdit && onMotionPathChange && onExitMotionPathEdit);
  const motionPathPreviews = useMemo(() => {
    if (previewMode || isMotionPathEditing) return [];
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) return [];

    const selectedSet = new Set(selectedIds);
    const previews: Array<{
      id: string;
      points: number[];
      start: { x: number; y: number };
      end: { x: number; y: number };
      autoRotate: boolean;
    }> = [];

    for (const el of elements) {
      if (!selectedSet.has(el.id)) continue;
      if (!el.motionPath || el.motionPath.points.length < 2) continue;
      try {
        const precomputed = precomputePath(el.motionPath);
        const samples = samplePathForRendering(precomputed, 60);
        if (samples.length < 2) continue;
        const points = samples.flatMap((p) => [p.x, p.y]);
        previews.push({
          id: el.id,
          points,
          start: samples[0],
          end: samples[samples.length - 1],
          autoRotate: !!el.motionPath.autoRotate,
        });
      } catch {
        // Ignore invalid paths
      }
    }

    return previews;
  }, [elements, isMotionPathEditing, previewMode, selectedIds]);

  const cameraOverlay = useMemo(() => {
    if (previewMode) return null;
    if (!allowCameraMotionV2) return null;
    const settings = cameraTrack?.settings;
    if (!settings?.enabled) return null;
    const overlays = settings.overlays;
    if (!overlays?.showReferenceBounds && !overlays?.showConstraintBounds) return null;

    const resolveBounds = (el: Element | undefined | null) => {
      if (!el) return null;
      const x = Number(el.x);
      const y = Number(el.y);
      const scaleX = Number.isFinite(el.scaleX) ? Number(el.scaleX) : 1;
      const scaleY = Number.isFinite(el.scaleY) ? Number(el.scaleY) : 1;
      const width = Math.abs(Number(el.width) * scaleX);
      const height = Math.abs(Number(el.height) * scaleY);
      if (!Number.isFinite(x) || !Number.isFinite(y) || width <= 0 || height <= 0) return null;
      return { x, y, width, height };
    };

    const referenceId =
      settings.referenceElementId ||
      settings.follow?.targetElementId ||
      (selectedIds.length === 1 ? selectedIds[0] : undefined);
    const referenceElement = referenceId ? elementsById.get(referenceId) : null;
    const referenceBounds =
      overlays.showReferenceBounds ? resolveBounds(referenceElement) : null;

    let constraintRect: { x: number; y: number; width: number; height: number } | null = null;
    if (overlays.showConstraintBounds && settings.constraints?.enabled) {
      const mode = settings.constraints?.mode ?? "contain";
      const marginX = mode === "safe-margin" ? Math.max(0, settings.constraints?.marginX ?? 0) : 0;
      const marginY = mode === "safe-margin" ? Math.max(0, settings.constraints?.marginY ?? 0) : 0;
      const width = canvasW - marginX * 2;
      const height = canvasH - marginY * 2;
      if (width > 0 && height > 0) {
        constraintRect = { x: marginX, y: marginY, width, height };
      }
    }

    if (!referenceBounds && !constraintRect) return null;
    return { referenceBounds, constraintRect };
  }, [allowCameraMotionV2, cameraTrack?.settings, canvasH, canvasW, elementsById, previewMode, selectedIds]);
  const activeTransitionCount = transitionEffectsRef?.current?.size ?? 0;
  const draftPreviewMode = getStoredDraftPreviewMode();
  const videoFilterPreviewQuality = useMemo(
    () =>
      resolveVideoFilterPreviewQuality({
        elements,
        clips: clips as Array<{ elementId?: string | null; start: number; end: number }>,
        playhead,
        activeTransitionCount,
        isPlaying,
        draftPreviewMode,
      }),
    [activeTransitionCount, clips, elements, isPlaying, playhead, draftPreviewMode]
  );
  const stagePixelRatio = useMemo(
    () =>
      resolveCanvasPlaybackPixelRatio({
        elements,
        clips: clips as Array<{ elementId?: string | null; start: number; end: number }>,
        playhead,
        activeTransitionCount,
        isPlaying,
        devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
        draftPreviewMode,
      }),
    [activeTransitionCount, clips, elements, isPlaying, playhead, draftPreviewMode]
  );
  /** Viewport bounds in canvas coordinates (with generous padding) */
  const viewportBounds = useMemo(() => {
    const padding = Math.max(canvasW, canvasH) * 0.5; // 50% padding for rotation/large elements
    return {
      left: -padding,
      top: -padding,
      right: canvasW + padding,
      bottom: canvasH + padding,
    };
  }, [canvasW, canvasH]);

  const showSafeZoneDebug = useMemo(() => {
    if (!import.meta.env.DEV) return false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("vb_safezone") === "1") return true;
      if (localStorage.getItem("vb:editor2:safezone") === "1") return true;
    } catch {}
    return false;
  }, []);

  const transformerSettings = useMemo(() => {
    const selectedElements = selectedIds.map(id => elementsById.get(id)).filter(Boolean) as Element[];
    if (selectedElements.length === 0) {
      return { keepRatio: false, perfectShapeIds: new Set<string>() };
    }

    const perfectShapeKinds = new Set(['circle', 'star', 'polygon']);
    const perfectShapeIds = new Set<string>();

    let allLocked = true;
    for (const el of selectedElements) {
      const isMedia = el.kind === 'image' || el.kind === 'video';
      const isText = el.kind === 'text';
      const isShape = ['rect', 'circle', 'star', 'polygon', 'arrow', 'line'].includes(el.kind);

      if (isMedia) {
        // Media always allows free transform (crop system handles)
        allLocked = false;
      } else if (isText) {
        // Text: scaleTextWithBox !== false means locked (default true)
        const textEl = el as import('../types').TextElement;
        if (textEl.scaleTextWithBox === false) {
          allLocked = false;
        }
      } else if (isShape) {
        // Shapes: freeformScale === true means unlocked
        if (el.freeformScale === true) {
          allLocked = false;
        }
        // Track perfect shapes that are locked
        if (perfectShapeKinds.has(el.kind) && el.freeformScale !== true) {
          perfectShapeIds.add(el.id);
        }
      }
    }

    return { keepRatio: allLocked, perfectShapeIds };
  }, [selectedIds, elementsById]);

	  // Pan mode (Space key held)
	  const [isPanning, setIsPanning] = useState(false);
	  const [isPanDragging, setIsPanDragging] = useState(false);
	  const panDragRestoreRef = useRef<{ node: Konva.Node; prevDraggable: boolean }[]>([]);
	  const activeTransformAnchorRef = useRef<string | null>(null);
	  // Track when transform is in progress to prevent cache invalidation during resize (prevents flash)
	  const isTransformingRef = useRef<boolean>(false);
	  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);
  const cropDragRef = useRef(new Map<string, { x: number; y: number; width: number; height: number }>());

  useEffect(() => {
    cropDragRef.current.clear();
  }, [activeCropId]);

  // Space bar for temporary pan mode
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Don't activate pan if typing in an input
      if (e.target && typeof (e.target as HTMLElement).closest === 'function' && (e.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsPanning(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsPanning(false);
        setIsPanDragging(false);

        // If user releases Space while dragging, stop pan cleanly
        const stage = stageRef.current;
        if (stage) stage.stopDrag();

        // Restore any temporarily disabled draggable nodes
        const items = panDragRestoreRef.current;
        if (items.length) {
          items.forEach(({ node, prevDraggable }) => {
            try { (node as any).draggable(prevDraggable); } catch {}
          });
          panDragRestoreRef.current = [];
        }
      }
    };
    const onBlur = () => { setIsPanning(false); setIsPanDragging(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Transformer nodes sync - optimized with RAF throttling
  useEffect(() => {
    // DEV-only effect diagnostics
    logEffect('CanvasStage', 'transformerSync',
      [selectedIds, elements, stageRef, trRef],
      ['selectedIds', 'elements', 'stageRef', 'trRef']
    );

    const stage = stageRef.current;
    const tr = trRef.current;
    if (!stage || !tr) return;

    // Use RAF to batch transformer updates
    const rafId = requestAnimationFrame(() => {
      const nodes = selectedIds
        .map(id => stage.findOne(`#node-${id}`))
        .filter(Boolean) as Konva.Node[];

      tr.nodes(nodes);
      markDirty();
    });

    return () => cancelAnimationFrame(rafId);
  }, [selectedIds, elements, stageRef, trRef]);

  // Wheel zoom handler - must use native listener with passive: false to allow preventDefault
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = canvasContainerRef.current;
    if (!el || previewMode || layout === 'plain') return;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        onZoom(e.deltaY > 0 ? -0.1 : 0.1);
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [onZoom, previewMode, layout]);

	  return (
	    <div
	      ref={canvasContainerRef}
	      className={layout === 'plain' ? "w-full h-full relative" : "flex-1 overflow-auto relative"}
	      data-canvas-col
	      data-stage-x={stagePos?.x ?? 0}
	      data-stage-y={stagePos?.y ?? 0}
        onContextMenu={previewMode ? undefined : (ev) => {
          // Fallback: handle right-click at the DOM level (more reliable across browsers/trackpads).
          // We synthesize a Konva-like event object and use Konva hit testing to find the clicked node.
          ev.preventDefault();
          ev.stopPropagation();

          const stage = stageRef.current as any;
          if (!stage) return;

          try {
            stage.setPointersPositions(ev.nativeEvent);
          } catch {}

          let target: any = stage;
          try {
            const pos = stage.getPointerPosition?.();
            const hit = pos ? stage.getIntersection?.(pos) : null;
            if (hit) target = hit;
          } catch {}

          try {
            onContextMenu?.({ evt: ev.nativeEvent, target, currentTarget: stage, type: "contextmenu" } as any);
          } catch {}
        }}
        onDragOver={layout === 'plain' || previewMode ? undefined : (e) => {
          if (e.dataTransfer?.types?.includes(VB_INSERT_DND_MIME)) {
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDrop={layout === 'plain' || previewMode ? undefined : (e) => {
          if (!e.dataTransfer) return;
          const detail = getVbInsertDetailFromDataTransfer(e.dataTransfer);
          if (!detail) return;
          e.preventDefault();
          window.dispatchEvent(new CustomEvent("vb:insert-media", { detail: { ...detail, mode: detail.mode ?? "append" } }));
        }}
    >
      <div className={layout === 'plain' ? "w-full h-full flex items-center justify-center" : "min-h-full flex items-center justify-center pt-6 pb-12 px-12"} data-workarea>
        <div
          className={layout === 'plain' ? "" : "rounded-xl p-4 shadow-inner"}
          style={layout === 'plain' ? undefined : (useEditorPalette ? {
            background: 'var(--vb-editor-canvas-wrap, rgba(39, 39, 42, 0.4))',
            border: '1px solid var(--vb-editor-border)',
          } : {
            background: 'rgba(39, 39, 42, 0.4)',
            border: '1px solid #09090b',
          })}
        >
          <div style={{ position: 'relative', width: canvasW * scale, height: canvasH * scale }}>
		          <Stage
		            ref={stageRef}
		            width={canvasW * scale}
		            height={canvasH * scale}
		            scaleX={scale}
		            scaleY={scale}
		            x={stagePos.x}
		            y={stagePos.y}
		            pixelRatio={stagePixelRatio}
	              listening={!previewMode}
            onMouseDown={previewMode ? undefined : (e) => {
              // Pan mode: Space + drag anywhere (even on top of elements)
              if (isPanning) {
                e.evt?.preventDefault?.();

                const stage = stageRef.current;
                if (!stage) return;

                // If user started drag on a draggable node, temporarily disable its drag
                const target = e.target;
                if (target && target !== stage) {
                  try {
                    const prev = (target as any).draggable?.() ?? false;
                    (target as any).draggable(false);
                    panDragRestoreRef.current = [{ node: target, prevDraggable: prev }];
                  } catch {
                    // ignore
                  }
                }

                setIsPanDragging(true);
                stage.startDrag();
                return;
              }

              // Mask edit mode: handle mask drawing
              if (isMaskEditing) {
                // Check if user clicked on a draggable handle (gradient handles, point handles)
                // If so, let Konva handle the drag natively instead of starting mask drawing
                const target = e.target;
                const isDraggableHandle = target && target !== stageRef.current &&
                  typeof (target as any).draggable === 'function' &&
                  (target as any).draggable();

                if (isDraggableHandle) {
                  // Let the handle's own drag handlers take over
                  return;
                }

                handleMaskMouseDown(e);
                return;
              }

              onStageMouseDown(e);
            }}
            onMouseMove={previewMode ? undefined : (e) => {
              if (isPanning) return;
              // Mask edit mode: handle mask drawing
              if (isMaskEditing && isDrawingMask) {
                handleMaskMouseMove(e);
                return;
              }
              onStageMouseMove();
            }}
            onMouseUp={previewMode ? undefined : (e) => {
              if (isPanning) {
                // Stop pan drag + restore node draggable state
                const stage = stageRef.current;
                if (stage) stage.stopDrag();

                const items = panDragRestoreRef.current;
                if (items.length) {
                  items.forEach(({ node, prevDraggable }) => {
                    try { (node as any).draggable(prevDraggable); } catch {}
                  });
                  panDragRestoreRef.current = [];
                }

                setIsPanDragging(false);
                return;
              }
              // Mask edit mode: handle mask drawing completion
              if (isMaskEditing) {
                handleMaskMouseUp();
                return;
              }
              onStageMouseUp();
            }}
            onDblClick={previewMode ? undefined : (e) => {
              // Mask edit mode: close path on double-click
              if (isMaskEditing) {
                e.cancelBubble = true;
                handleMaskDblClick();
              }
            }}
	            onDragEnd={(e) => {
	              // Only update stage position when the Stage itself was dragged (panning).
	              // Child node dragend events bubble up here — ignore them to prevent
	              // the canvas from panning to the child node's coordinates on drop.
	              if (setStagePos && e.target === stageRef.current) {
	                setStagePos({ x: e.target.x(), y: e.target.y() });
	              }
	            }}
	            draggable={isPanning && !previewMode}
	            className={layout === 'plain'
	              ? (isLightWorkspace ? "bg-white" : "bg-black")
	              : ((isLightWorkspace ? "bg-white" : "bg-black") + " rounded-lg shadow-2xl")}
	            style={{
                borderRadius: layout === 'plain' ? 0 : 12,
                cursor: layout === 'plain' ? 'default' : (
                  isPanning ? (isPanDragging ? 'grabbing' : 'grab') :
                  isMaskEditing ? 'crosshair' : 'default'
                ),
              }}
	          >
            <Layer ref={layerRef} {...cameraLayerTransform}>
              {/* Render canvas elements */}
              {elements.map((el) => {
                // FIX: Keep elements mounted to prevent flash on visibility change.
                // Use Konva's native `visible` prop instead of conditional rendering.
                // This ensures frame continuity - no unmount/remount cycles.
                const isVisible = isElementEffectivelyVisible(el);

                // Viewport culling — skip expensive computation for offscreen elements.
                // Only cull when visible (invisible elements are already cheap via visible={false}).
                if (isVisible && !isInViewport(el, viewportBounds)) {
                  return <React.Fragment key={el.id} />;
                }

                // Use pre-defined blend mode map for performance
                // Pattern elements store blend mode in patternConfig.blendMode, not el.blendMode
                const elBlendMode = (el as any).isPattern
                  ? (el as any).patternConfig?.blendMode
                  : (el as any).blendMode;
                const globalCompositeOperation: GlobalCompositeOperation = elBlendMode
                  ? BLEND_MODE_MAP[elBlendMode] || 'source-over'
                  : 'source-over';

                // Apply keyframe animations (Phase 1.5)
                // IMPORTANT: Don't set opacity to 0 when not visible - use Konva's visible prop only
                // Setting opacity=0 here causes flash when visibility changes via direct Konva update
                const baseProps = {
                  x: el.x,
                  y: el.y,
                  scaleX: el.scaleX ?? 1,
                  scaleY: el.scaleY ?? 1,
                  rotation: el.rotation ?? 0,
                  opacity: el.opacity ?? 1,
                };
                const animatedProps = applyKeyframeAnimations(el, playhead, baseProps, { elementsById, clips });

                // Apply transition effects (wipe, dissolve, slide, etc.)
                const transition = transitionEffectsRef?.current?.get(el.id);
                if (transition) {
                  animatedProps.x += transition.offsetX;
                  animatedProps.y += transition.offsetY;
                  animatedProps.scaleX *= transition.scale;
                  animatedProps.scaleY *= transition.scale;
                  animatedProps.opacity *= transition.opacity;
                  animatedProps.rotation += transition.rotation;
                }

                const isCropTarget = isCropActive && activeCropId === el.id;

                // 3D Transform Support: Apply pseudo-3D transforms using skew/scale
                // This converts rotateX/rotateY/perspective values to 2D skew approximations
                const element3D = {
                  rotateX: (el as any).rotateX,
                  rotateY: (el as any).rotateY,
                  rotateZ: (el as any).rotateZ,
                  perspective: (el as any).perspective,
                  depthShadow: (el as any).depthShadow,
                };
                const has3D = has3DTransformValues(element3D);
                const transform3D = has3D
                  ? get3DNodeAttrs(element3D, animatedProps.scaleX, animatedProps.scaleY, animatedProps.rotation)
                  : null;

                // Check if this element is being mask-edited
                const isMaskEditTarget = isMaskEditing && maskEditMode?.elementId === el.id;

                // Center-pivot rotation: offset by half-dimensions so rotation
                // pivots around the element center instead of top-left corner.
                const halfW = (el.width ?? 0) / 2;
                const halfH = (el.height ?? 0) / 2;

                // When an element is actively being dragged, Konva's imperative
                // drag position (set by onDragMove) is authoritative. React must
                // NOT overwrite x/y/scale/rotation — otherwise the node jumps back
                // to the React-state position on every re-render during drag.
                const isBeingDragged = activeDragRef?.current?.has(el.id) ?? false;

	                const common: KonvaNodeCommonProps & { onTap?: (evt: Konva.KonvaEventObject<TouchEvent>) => void; visible?: boolean; listening?: boolean } = {
	                  id: `node-${el.id}`,
	                  name: "vb-node",
	                  // FIX: Use Konva's native visible prop for instant visibility (no React reconciliation)
	                  // This prevents single-frame flash when crossing clip boundaries
	                  visible: isVisible,
	                  // Disable interactions when not visible to prevent ghost clicks
	                  listening: isVisible,
	                  // Disable dragging when mask editing this element
	                  draggable: isVisible && !el.locked && !isCropActive && !isMaskEditTarget,
	                  // Position + transform: skip when dragging to prevent React overwriting Konva's drag state
	                  ...(isBeingDragged ? {} : {
	                    x: animatedProps.x + halfW,
	                    y: animatedProps.y + halfH,
	                    offsetX: halfW,
	                    offsetY: halfH,
	                    scaleX: transform3D?.scaleX ?? animatedProps.scaleX,
	                    scaleY: transform3D?.scaleY ?? animatedProps.scaleY,
	                    rotation: transform3D?.rotation ?? animatedProps.rotation,
	                  }),
	                  opacity: animatedProps.opacity,
	                  // 3D pseudo-transform properties - MUST explicitly reset to 0/false when no 3D
	                  // Otherwise Konva nodes retain previous values when receiving undefined
	                  skewX: transform3D?.skewX ?? 0,
	                  skewY: transform3D?.skewY ?? 0,
	                  shadowColor: transform3D?.shadowColor ?? undefined,
	                  shadowBlur: transform3D?.shadowBlur ?? 0,
	                  shadowOffsetX: transform3D?.shadowOffsetX ?? 0,
	                  shadowOffsetY: transform3D?.shadowOffsetY ?? 0,
	                  shadowOpacity: transform3D?.shadowOpacity ?? 0,
	                  shadowEnabled: transform3D?.shadowEnabled ?? false,
	                  globalCompositeOperation,
	                  onMouseEnter: previewMode ? undefined : () => setHoveredElementId(el.id),
	                  onMouseLeave: previewMode ? undefined : () => setHoveredElementId((cur) => (cur === el.id ? null : cur)),
	                  onDragStart: !isCropActive && !isMaskEditTarget && onNodeDragStart ? (evt) => onNodeDragStart(el.id, evt) : undefined,
	                  onDragMove: !isCropActive && !isMaskEditTarget && onNodeDragMove ? (evt) => onNodeDragMove(el.id, evt) : undefined,
	                  onDragEnd: !isCropActive && !isMaskEditTarget ? (evt) => onNodeDragEnd(el.id, evt) : undefined,
	                  onTransformEnd: !isCropActive ? (evt) => onNodeTransformEnd(el.id, evt.target as Konva.Node) : undefined,
	                  onMouseDown: (evt) => {
                    if (isCropActive && !isCropTarget) return;
                    // When mask editing this element, let the event bubble up to Stage for mask drawing
                    if (isMaskEditTarget) {
                      // Don't cancel bubble - let Stage handle the mask drawing
                      return;
                    }
                    evt.cancelBubble = true;
                    const isMulti = evt.evt?.shiftKey || evt.evt?.metaKey || evt.evt?.ctrlKey;
                    onSelectElement(el.id, isMulti);
                  },
                  onTap: (evt) => {
                    if (isCropActive && !isCropTarget) return;
                    // When mask editing this element, let the event bubble up
                    if (isMaskEditTarget) return;
                    const isMulti = evt.evt?.shiftKey || evt.evt?.metaKey || evt.evt?.ctrlKey;
                    onSelectElement(el.id, isMulti);
                  },
                };
                // Render the element by kind
                let rendered: React.ReactNode = null;
                if (el.kind === "rect") {
                  rendered = renderRectElement({
                    el: el as RectEl,
                    common,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                  });
                } else if (el.kind === "circle") {
                  rendered = renderCircleElement({
                    el: el as CircleEl,
                    common,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                  });
                } else if (el.kind === "text") {
                  rendered = renderTextElement({
                    el: el as TextEl,
                    common,
                    fontPair,
                    editingTextId,
                  });
                } else if (el.kind === "image") {
                  rendered = renderImageElement({
                    el: el as ImageEl,
                    common,
                    globalCompositeOperation,
                    runtime,
                    faceBlurCacheRef,
                    gradientFadeCacheRef,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                    cropGuidesEnabled,
                    cropGuideMode,
                    isCropTarget,
                    cropDragRef,
                    updateEl,
                  });
                } else if (el.kind === "video") {
                  rendered = renderVideoElement({
                    el: el as VideoEl,
                    common,
                    globalCompositeOperation,
                    runtime,
                    faceBlurCacheRef,
                    gradientFadeCacheRef,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                    cropGuidesEnabled,
                    cropGuideMode,
                    isCropTarget,
                    cropDragRef,
                    updateEl,
                    videoFilterPreviewQuality,
                    previewTraceEnabled,
                    isPlaying,
                  });
                } else if (el.kind === "line") {
                  rendered = renderLineElement({
                    el: el as LineEl,
                    common,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                  });
                } else if (el.kind === "star") {
                  rendered = renderStarElement({
                    el: el as StarEl,
                    common,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                  });
                } else if (el.kind === "polygon") {
                  rendered = renderPolygonElement({
                    el: el as PolygonEl,
                    common,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                  });
                } else if (el.kind === "arrow") {
                  rendered = renderArrowElement({
                    el: el as ArrowEl,
                    common,
                    hoveredElementId,
                    totalViewportScale,
                    isTransformingRef,
                  });
                }

                if (!rendered) return null;

                // Wrap in Group with clipFunc for wipe/morph transitions
                if (transition?.clipPath) {
                  return (
                    <Group
                      key={`clip-${el.id}`}
                      clipFunc={(ctx) => transition.clipPath?.(ctx as unknown as CanvasRenderingContext2D)}
                    >
                      {rendered}
                    </Group>
                  );
                }

                return rendered;
              })}

            </Layer>

            {/* Grid overlay: above elements, non-interactive, excluded from export */}
            <Layer id="vb-grid-overlay" listening={false} visible={!previewMode} {...cameraLayerTransform}>
              {showGuides && (
                <>
                  {/* Optional center guides (disabled by default) */}
                  <Rect
                    x={canvasW / 2 - 1}
                    y={0}
                    width={1}
                    height={canvasH}
                    fill="rgba(255,255,255,0.9)"
                    listening={false}
                  />
                  <Rect
                    x={canvasW / 2 + 1}
                    y={0}
                    width={1}
                    height={canvasH}
                    fill="rgba(255,255,255,0.9)"
                    listening={false}
                  />

                  {/* 🎨 Grid Lines (memoized for performance) */}
                  {gridLines}
                </>
              )}
              {showSafeZoneDebug &&
                (() => {
                  const safe = computeSafeZoneRect({ canvasW, canvasH, marginFrac: 0.06 });
                  return (
                    <Rect
                      x={safe.x}
                      y={safe.y}
                      width={safe.width}
                      height={safe.height}
                      stroke="rgba(239,68,68,0.85)"
                      strokeWidth={2 / Math.max(0.001, totalViewportScale)}
                      dash={[10 / Math.max(0.001, totalViewportScale), 8 / Math.max(0.001, totalViewportScale)]}
                      listening={false}
                    />
                  );
                })()}
              {cameraOverlay?.constraintRect && (
                <Rect
                  x={cameraOverlay.constraintRect.x}
                  y={cameraOverlay.constraintRect.y}
                  width={cameraOverlay.constraintRect.width}
                  height={cameraOverlay.constraintRect.height}
                  stroke="rgba(245, 158, 11, 0.85)"
                  strokeWidth={1 / Math.max(0.001, totalViewportScale)}
                  dash={[
                    10 / Math.max(0.001, totalViewportScale),
                    8 / Math.max(0.001, totalViewportScale),
                  ]}
                  strokeScaleEnabled={false}
                  listening={false}
                />
              )}
              {cameraOverlay?.referenceBounds && (
                <Rect
                  x={cameraOverlay.referenceBounds.x}
                  y={cameraOverlay.referenceBounds.y}
                  width={cameraOverlay.referenceBounds.width}
                  height={cameraOverlay.referenceBounds.height}
                  stroke="rgba(59, 130, 246, 0.85)"
                  strokeWidth={1 / Math.max(0.001, totalViewportScale)}
                  dash={[
                    6 / Math.max(0.001, totalViewportScale),
                    5 / Math.max(0.001, totalViewportScale),
                  ]}
                  strokeScaleEnabled={false}
                  listening={false}
                />
              )}
              {/* Boostplate placeholder overlay */}
              {clips && clips.length > 0 && (
                <BoostplateOverlay
                  clips={clips}
                  playhead={playhead}
                  canvasW={canvasW}
                  canvasH={canvasH}
                  totalViewportScale={totalViewportScale}
                />
              )}
            </Layer>

            {/* UI overlay layer (transformer, marquee, guides) */}
            {!previewMode && (
            <Layer id="vb-ui-overlay" {...cameraLayerTransform}>
              {/* Motion Path preview (non-edit mode) */}
              {motionPathPreviews.map((preview) => (
                <Group key={`motion-path-preview-${preview.id}`} listening={false}>
                  <Line
                    points={preview.points}
                    stroke={preview.autoRotate ? "rgba(34, 197, 94, 0.85)" : "rgba(139, 92, 246, 0.85)"}
                    strokeWidth={2 / Math.max(0.001, totalViewportScale)}
                    dash={[8 / Math.max(0.001, totalViewportScale), 4 / Math.max(0.001, totalViewportScale)]}
                    lineCap="round"
                    lineJoin="round"
                    perfectDrawEnabled={false}
                  />
                  <Circle
                    x={preview.start.x}
                    y={preview.start.y}
                    radius={4 / Math.max(0.001, totalViewportScale)}
                    fill="rgba(34, 197, 94, 0.95)"
                    stroke="#fff"
                    strokeWidth={1 / Math.max(0.001, totalViewportScale)}
                    perfectDrawEnabled={false}
                  />
                  <Circle
                    x={preview.end.x}
                    y={preview.end.y}
                    radius={4 / Math.max(0.001, totalViewportScale)}
                    fill="rgba(239, 68, 68, 0.95)"
                    stroke="#fff"
                    strokeWidth={1 / Math.max(0.001, totalViewportScale)}
                    perfectDrawEnabled={false}
                  />
                </Group>
              ))}

              {/* Smart Hover Box - shows colored outline based on element type */}
              {hoveredElementId && !selectedIds.includes(hoveredElementId) && (() => {
                const hoveredEl = elementsById.get(hoveredElementId);
                if (!hoveredEl) return null;

                const hoverColor = getHoverColor(hoveredEl.kind);
                const strokeW = 2 / Math.max(0.001, totalViewportScale);

                // All elements use top-left positioning (x,y = top-left of bounding box).
                // Inner shapes (circle, star, polygon) center within their Group via negative offsets,
                // but the Group itself always uses top-left coords from common props.
                return (
                  <Rect
                    x={hoveredEl.x}
                    y={hoveredEl.y}
                    width={hoveredEl.width}
                    height={hoveredEl.height}
                    scaleX={hoveredEl.scaleX ?? 1}
                    scaleY={hoveredEl.scaleY ?? 1}
                    rotation={hoveredEl.rotation || 0}
                    stroke={hoverColor}
                    strokeWidth={strokeW}
                    strokeScaleEnabled={false}
                    fill="transparent"
                    listening={false}
                    perfectDrawEnabled={false}
                    shadowColor={hoverColor}
                    shadowBlur={4 / Math.max(0.001, totalViewportScale)}
                    shadowOpacity={0.5}
                  />
                );
              })()}

              {!isCropActive && (
                <>
                 
                 
                 
                 
                 {/* Transformer for single or multiple nodes */}
<Transformer
  ref={trRef}
  rotateEnabled
  rotateAnchorOffset={Math.max(28 / scale, 28)}
  rotateAnchorCursor="grab"
  rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
  rotateLineVisible={true}
  centeredScaling={false}
  onTransformStart={() => {
    activeTransformAnchorRef.current = (trRef.current as any)?.getActiveAnchor?.() ?? null;
    // Set transforming flag to prevent cache invalidation during resize (prevents flash)
    isTransformingRef.current = true;

    // 🔒 Freeze timeline geometry writes during active transform
    window.dispatchEvent(new CustomEvent("vb:canvas-transform-start"));

    // 🎯 Notify parent that transform started for each selected element
    if (onNodeTransformStart) {
      const nodes = trRef.current?.nodes() ?? [];
      nodes.forEach((node) => {
        // Strip "node-" prefix from Konva node ID to get element ID
        const id = node.id()?.replace(/^node-/, '');
        if (id) onNodeTransformStart(id);
      });
    }
  }}
  onTransform={() => {
    activeTransformAnchorRef.current =
      (trRef.current as any)?.getActiveAnchor?.() ?? activeTransformAnchorRef.current;
    // 🎯 Notify parent with active anchor for canvas-local resize math
    if (onNodeTransform) {
      const nodes = trRef.current?.nodes() ?? [];
      // Strip "node-" prefix from Konva node IDs to get element IDs
      const ids = nodes.map((node) => node.id()?.replace(/^node-/, '')).filter(Boolean) as string[];
      const anchor = activeTransformAnchorRef.current;
      onNodeTransform(ids, anchor);
    }
  }}
  onTransformEnd={() => {
    // ✅ Persist final geometry into element state (the missing piece)
    if (onNodeTransformEnd) {
      const nodes = trRef.current?.nodes() ?? [];
      nodes.forEach((node) => {
        // Strip "node-" prefix from Konva node ID to get element ID
        const id = node.id()?.replace(/^node-/, '');
        if (id) onNodeTransformEnd(id, node);
      });
    }

    activeTransformAnchorRef.current = null;

    // 🔒 FLASH-FIX: Delay clearing transform flag by 2 frames
    // This allows React state to settle before FilteredImage cache effects run
    // Without this delay, cache() calls happen while state is still reconciling,
    // causing a brief visual flash on transform end
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isTransformingRef.current = false;
      });
    });

    // 🔓 Unfreeze timeline geometry writes
    window.dispatchEvent(new CustomEvent("vb:canvas-transform-end"));
  }}
  enabledAnchors={[
    "top-left", "top-right", "bottom-left", "bottom-right",
    "middle-left", "middle-right", "top-center", "bottom-center"
  ]}
  keepRatio={transformerSettings.keepRatio}
  anchorStroke="#4F8CFF"
  anchorFill="#FFFFFF"
  anchorSize={Math.max(10 / scale, 8)}
  anchorCornerRadius={Math.max(5 / scale, 4)}
  anchorStrokeWidth={Math.max(1.5 / scale, 1)}
  borderStroke="#4F8CFF"
  borderStrokeWidth={Math.max(1.5 / scale, 1)}
  borderDash={[Math.max(8 / scale, 4), Math.max(4 / scale, 2)]}
  padding={0}
  anchorStyleFunc={(anchor: any) => {
    // Drop shadow on all handles for premium depth
    anchor.shadowColor('rgba(0, 0, 0, 0.25)');
    anchor.shadowBlur(Math.max(4 / scale, 3));
    anchor.shadowOffsetX(0);
    anchor.shadowOffsetY(Math.max(1 / scale, 0.5));
    anchor.shadowEnabled(true);

    // Rotation handle — distinct circular style with glow
    if (anchor.hasName('rotater')) {
      const rSize = Math.max(14 / scale, 10);
      anchor.width(rSize);
      anchor.height(rSize);
      anchor.offsetX(rSize / 2);
      anchor.offsetY(rSize / 2);
      anchor.cornerRadius(rSize / 2);
      anchor.fill('#FFFFFF');
      anchor.stroke('#4F8CFF');
      anchor.strokeWidth(Math.max(2 / scale, 1.5));
      anchor.shadowColor('rgba(79, 140, 255, 0.5)');
      anchor.shadowBlur(Math.max(8 / scale, 6));
    }

    // Corner handles — slightly larger circles
    const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
    if (corners.some(n => anchor.hasName(n))) {
      const cSize = Math.max(10 / scale, 8);
      anchor.width(cSize);
      anchor.height(cSize);
      anchor.offsetX(cSize / 2);
      anchor.offsetY(cSize / 2);
      anchor.cornerRadius(cSize / 2);
    }

    // Edge handles — smaller rounded rectangles
    const edges = ['middle-left', 'middle-right', 'top-center', 'bottom-center'];
    if (edges.some(n => anchor.hasName(n))) {
      const isVertical = anchor.hasName('middle-left') || anchor.hasName('middle-right');
      const eW = isVertical ? Math.max(6 / scale, 5) : Math.max(12 / scale, 10);
      const eH = isVertical ? Math.max(12 / scale, 10) : Math.max(6 / scale, 5);
      anchor.width(eW);
      anchor.height(eH);
      anchor.offsetX(eW / 2);
      anchor.offsetY(eH / 2);
      anchor.cornerRadius(Math.max(3 / scale, 2));
    }
  }}
  // Media transforms stay visual-only here; perfect shapes remain square when locked.
  boundBoxFunc={(oldBox, newBox) => {
    const nodes = trRef.current?.nodes() ?? [];

    // Check if any selected element is media
    const hasMedia = nodes.some((node) => {
      const id = node.id()?.replace(/^node-/, '');
      if (!id) return false;
      const el = elementsById.get(id);
      return el?.kind === 'video' || el?.kind === 'image';
    });

    if (hasMedia) {
      return {
        ...newBox,
      };
    }

    // 🔒 Perfect shape enforcement: circle/star/polygon stay square when locked
    const hasPerfectShape = nodes.some((node) => {
      const id = node.id()?.replace(/^node-/, '');
      return id && transformerSettings.perfectShapeIds.has(id);
    });

    if (hasPerfectShape && nodes.length === 1) {
      // Enforce width === height for perfect shapes
      const widthDelta = Math.abs(newBox.width - oldBox.width);
      const heightDelta = Math.abs(newBox.height - oldBox.height);

      // Use the dimension that changed more (user's intent)
      const size = widthDelta >= heightDelta ? Math.abs(newBox.width) : Math.abs(newBox.height);

      // Adjust position to keep the shape centered on the resize anchor
      const widthDiff = size - Math.abs(newBox.width);
      const heightDiff = size - Math.abs(newBox.height);

      return {
        ...newBox,
        width: newBox.width < 0 ? -size : size,
        height: newBox.height < 0 ? -size : size,
        x: newBox.x - widthDiff / 2,
        y: newBox.y - heightDiff / 2,
      };
    }

    // For non-media elements: use default behavior
    return newBox;
  }}
/>











                {/* Marquee selection rectangle */}
                {selectionRect.visible && (
                  <Rect
                    ref={selRectRef}
                    x={selectionRect.x}
                    y={selectionRect.y}
                    width={selectionRect.width}
                    height={selectionRect.height}
                    stroke="#4F8CFF"
                    dash={[4, 4]}
                    strokeWidth={1}
                    fill="rgba(79,140,255,0.08)"
                    listening={false}
                  />
                )}
                </>
              )}

              {/* Motion Path edit overlay */}
              {!isCropActive && isMotionPathEditing && motionPathEdit && (
                <PathEditorOverlay
                  path={motionPathEdit.path}
                  scale={Math.max(0.001, totalViewportScale)}
                  canvasW={canvasW}
                  canvasH={canvasH}
                  gridSize={50}
                  snapToGrid={!!showGuides}
                  onPathChange={(next) => onMotionPathChange?.(motionPathEdit.elementId, next)}
                  onExitEditMode={() => onExitMotionPathEdit?.()}
                />
              )}

              {/* Mask Editor overlay for Gradient Fade */}
              {isMaskEditing && maskEditElement && (
                <MaskEditorOverlay
                  elementX={maskEditElement.x}
                  elementY={maskEditElement.y}
                  elementWidth={maskEditElement.width}
                  elementHeight={maskEditElement.height}
                  elementRotation={maskEditElement.rotation || 0}
                  mask={(maskEditElement as any).opacitySettings?.mask || DEFAULT_VECTOR_MASK}
                  gradient={(maskEditElement as any).opacitySettings?.gradient}
                  editState={{
                    elementId: maskEditMode.elementId,
                    tool: maskEditMode.tool,
                    operation: maskEditMode.operation,
                    activePath: activeMaskPath,
                    selectedPathId: null,
                    selectedPointIndex: null,
                    showOverlayPreview: false,
                  }}
                  activePath={activeMaskPath}
                  onPointDrag={handleMaskPointDrag}
                  onHandleDrag={handleMaskHandleDrag}
                  onGradientHandleDrag={handleGradientHandleDrag}
                  showOverlayPreview={false}
                />
              )}

              {/* 🎨 PHASE 2.3: Group Indicators */}
              {groups && Array.from(groups.entries()).map(([groupId, memberIds]) => {
                // Calculate group bounding box
                const groupElements = elements.filter(el => memberIds.includes(el.id));
                if (groupElements.length === 0) return null;

                const allCorners: {x: number; y: number}[] = [];
                groupElements.forEach(el => {
                  const cx = el.x + el.width / 2;
                  const cy = el.y + el.height / 2;
                  const rad = ((el.rotation || 0) * Math.PI) / 180;
                  const hw = el.width / 2;
                  const hh = el.height / 2;
                  ([ [-hw,-hh],[hw,-hh],[hw,hh],[-hw,hh] ] as [number,number][]).forEach(([dx, dy]) => {
                    allCorners.push({
                      x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
                      y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
                    });
                  });
                });
                const left = Math.min(...allCorners.map(c => c.x));
                const top = Math.min(...allCorners.map(c => c.y));
                const right = Math.max(...allCorners.map(c => c.x));
                const bottom = Math.max(...allCorners.map(c => c.y));

                const padding = 5;

                return (
                  <Rect
                    key={`group-${groupId}`}
                    x={left - padding}
                    y={top - padding}
                    width={right - left + padding * 2}
                    height={bottom - top + padding * 2}
                    stroke="#a78bfa"
                    strokeWidth={2 / scale}
                    dash={[8 / scale, 4 / scale]}
                    opacity={0.6}
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                );
              })}

              {/* 🎨 PHASE 2.2: Smart Guides - Enhanced Visibility */}
              {smartGuides.map(guide => {
                if (guide.type === 'vertical') {
                  return (
                    <Line
                      key={guide.id}
                      points={[guide.position, 0, guide.position, canvasH]}
                      stroke={guide.color}
                      strokeWidth={2 / scale}
                      dash={[15 / scale, 8 / scale]}
                      opacity={0.95}
                      listening={false}
                      perfectDrawEnabled={false}
                      shadowColor="rgba(0,0,0,0.3)"
                      shadowBlur={4}
                      shadowOffsetX={0}
                      shadowOffsetY={0}
                    />
                  );
                } else {
                  return (
                    <Line
                      key={guide.id}
                      points={[0, guide.position, canvasW, guide.position]}
                      stroke={guide.color}
                      strokeWidth={2 / scale}
                      dash={[15 / scale, 8 / scale]}
                      opacity={0.95}
                      listening={false}
                      perfectDrawEnabled={false}
                      shadowColor="rgba(0,0,0,0.3)"
                      shadowBlur={4}
                      shadowOffsetX={0}
                      shadowOffsetY={0}
                    />
                  );
                }
              })}
            </Layer>
            )}
          </Stage>
            <WebGLFxLayer
              width={canvasW * scale}
              height={canvasH * scale}
              enabled={webGLFxEnabled}
              uniforms={globalFxUniforms}
              sourceCanvas={stageRef.current?.getStage?.()?.container?.()?.querySelector?.('canvas') ?? null}
            />
            <PatternAnimLayer
              elements={elements}
              canvasX={stagePos.x}
              canvasY={stagePos.y}
              canvasScale={scale}
              canvasW={canvasW}
              canvasH={canvasH}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(CanvasStage, (prevProps, nextProps) => {
  const keys = Object.keys(nextProps) as (keyof CanvasStageProps)[];
  for (const key of keys) {
    if (key === 'playhead') {
      const playheadChanged = prevProps.playhead !== nextProps.playhead;
      if (!playheadChanged) continue;
      if (hasActiveCanvasFrameDependency({
        elements: nextProps.elements,
        clips: nextProps.clips as Array<{ elementId?: string | null; start: number; end: number }>,
        playhead: nextProps.playhead ?? 0,
        activeTransitionCount: nextProps.transitionEffectsRef?.current?.size ?? 0,
      })) return false;
      continue;
    }
    if (key === 'transitionEffectsRef') continue;
    if (prevProps[key] !== nextProps[key]) return false;
  }
  return true;
});
